import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Cable,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  Cpu,
  Eye,
  EyeOff,
  ExternalLink,
  Gauge,
  Globe,
  Key,
  Layers,
  Lock,
  MapPin,
  Network,
  Power,
  PowerOff,
  Radio,
  RefreshCw,
  Router,
  Save,
  Server,
  ShieldCheck,
  Sliders,
  Trash2,
  Tv,
  Wifi,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { openOnuWebAdminLink } from "../../utils/openOnuWebAdmin.ts";
import { toast } from "../../utils/notify.ts";
import {
  CustomerSmartOLTProfile,
  SmartOLTEthernetPort,
  SmartOLTWifiPort,
  SmartOLTTopology,
  smartoltService,
} from "../../services/smartolt.service.ts";
import { LiveTrafficTestConsole } from "../smartolt/LiveTrafficTestConsole";
import {
  SmartOLTActionModals,
  ModalType,
  ModalContextData,
} from "../smartolt/SmartOLTActionModals.tsx";

type Props = {
  customerId: string;
};

type GraphType = "hourly" | "daily" | "weekly" | "monthly" | "yearly";

const actionOptions = [
  { key: "reboot_onu_by_external_id", label: "Reboot ONU" },
  { key: "resync_onu_config_by_external_id", label: "Resync ONU Config" },
  { key: "enable_onu_by_external_id", label: "Enable ONU (Administrative)" },
  { key: "disable_onu_by_external_id", label: "Disable ONU (Administrative)" },
];

export const CustomerSmartOLTSection: React.FC<Props> = ({ customerId }) => {
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphImageLoading, setGraphImageLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState<"6h" | "24h" | "7d" | "30d">("24h");
  const [graphType, setGraphType] = useState<GraphType>("hourly");
  const [graphKind, setGraphKind] = useState<"signal" | "traffic">("traffic");
  const [activeGraphTab, setActiveGraphTab] = useState<"historical" | "live_test">("historical");
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [profile, setProfile] = useState<CustomerSmartOLTProfile | null>(null);
  const [graphs, setGraphs] = useState<any>(null);
  const [commands, setCommands] = useState<any[]>([]);
  const [graphImageUrl, setGraphImageUrl] = useState<string | null>(null);
  const graphImageUrlRef = useRef<string | null>(null);
  const [mappingForm, setMappingForm] = useState({ onu_external_id: "", sn: "", mac: "" });
  const [selectedAction, setSelectedAction] = useState(actionOptions[0].key);
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [webAdminBusy, setWebAdminBusy] = useState(false);

  // Configuration and Action Modals Suite State
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [topology, setTopology] = useState<SmartOLTTopology | null>(null);
  const [modalContext, setModalContext] = useState<ModalContextData | null>(null);
  const [actionCategory, setActionCategory] = useState<"quick" | "wan" | "lan" | "security" | "services">("quick");

  const loadGraphImage = useCallback(async () => {
    if (!profile?.mapping?.exists) {
      if (graphImageUrlRef.current) {
        URL.revokeObjectURL(graphImageUrlRef.current);
        graphImageUrlRef.current = null;
      }
      setGraphImageUrl(null);
      return;
    }
    setGraphImageLoading(true);
    try {
      const url = await smartoltService.getCustomerGraphImage(customerId, {
        kind: graphKind,
        graph_type: graphType,
      });
      const previousUrl = graphImageUrlRef.current;
      graphImageUrlRef.current = url;
      setGraphImageUrl(url);
      if (previousUrl) URL.revokeObjectURL(previousUrl);
    } catch (error) {
      console.error(error);
      if (!graphImageUrlRef.current) {
        setGraphImageUrl(null);
      }
    } finally {
      setGraphImageLoading(false);
    }
  }, [customerId, graphKind, graphType, profile?.mapping?.exists]);

  const loadGraphs = useCallback(async () => {
    if (!profile?.mapping?.exists) {
      setGraphs({ mapped: false, series: { signal_rx: [], signal_tx: [], traffic_up: [], traffic_down: [] } });
      return;
    }

    setGraphLoading(true);
    try {
      const g = await smartoltService.getCustomerGraphs(customerId, range);
      setGraphs(g);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load CPE ONU telemetry history");
    } finally {
      setGraphLoading(false);
    }
  }, [customerId, profile?.mapping?.exists, range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await smartoltService.getCustomerProfile(customerId);
      setProfile(p);
      setMappingForm({
        onu_external_id: p?.mapping?.onu_external_id || p?.suggested_mapping?.onu_external_id || "",
        sn: p?.mapping?.sn || p?.suggested_mapping?.sn || "",
        mac: p?.mapping?.mac || p?.suggested_mapping?.mac || "",
      });

      const c = await smartoltService.getCustomerCommands(customerId);
      setCommands(c?.items || []);

      // Auto-live sync with 60-second cooldown:
      // If customer is mapped and last sync is older than 60s (or never synced),
      // seamlessly trigger a live CPE ONU sync without blocking initial UI paint.
      const syncAge = p?.cards?.sync_age_minutes;
      if (p?.mapping?.exists && (syncAge === null || syncAge === undefined || syncAge >= 1)) {
        setAutoSyncing(true);
        smartoltService
          .refreshCustomerFromSmartOLT(customerId, { force: false, cooldown_seconds: 60 })
          .then(async (refreshRes) => {
            if (refreshRes?.live) {
              const fresh = await smartoltService.getCustomerProfile(customerId);
              setProfile(fresh);
            }
          })
          .catch((err) => {
            console.warn("Background auto-live sync check:", err);
          })
          .finally(() => {
            setAutoSyncing(false);
          });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load CPE ONU profile data");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadGraphs();
  }, [loadGraphs]);

  useEffect(() => {
    loadGraphImage();
  }, [loadGraphImage]);

  useEffect(() => {
    return () => {
      if (graphImageUrlRef.current) {
        URL.revokeObjectURL(graphImageUrlRef.current);
        graphImageUrlRef.current = null;
      }
    };
  }, []);

  const handleCopy = (key: string, text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const togglePasswordVisibility = (portKey: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [portKey]: !prev[portKey],
    }));
  };

  const applySuggestedMapping = () => {
    const suggestion = profile?.suggested_mapping;
    if (!suggestion?.onu_external_id) return;
    setMappingForm({
      onu_external_id: suggestion.onu_external_id,
      sn: suggestion.sn || "",
      mac: suggestion.mac || "",
    });
    setShowMappingEditor(true);
  };

  const saveMapping = async (force = false) => {
    if (!mappingForm.onu_external_id.trim()) {
      toast.error("Unique External ID is required");
      return;
    }
    setBusy(true);
    try {
      await smartoltService.updateCustomerMapping(customerId, {
        ...mappingForm,
        force_remap: force,
      });
      toast.success("CPE ONU mapping saved successfully");
      setShowMappingEditor(false);
      await load();
    } catch (error: any) {
      if (error?.response?.status === 409 && !force) {
        const confirmed = window.confirm(
          "This ONU is mapped to another customer. Confirm re-mapping to this customer?",
        );
        if (confirmed) {
          await saveMapping(true);
        }
        return;
      }
      toast.error(error?.response?.data?.error || "Failed to update mapping");
    } finally {
      setBusy(false);
    }
  };

  const refreshFromSmartOLT = async () => {
    if (!profile?.mapping?.exists) {
      toast.error("Map customer to an ONU first");
      return;
    }
    setRefreshing(true);
    try {
      await smartoltService.refreshCustomerFromSmartOLT(customerId, { force: true });
      toast.success("CPE ONU live details synchronized");
      await load();
      await loadGraphImage();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to refresh CPE ONU details");
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch CPE ONU topology (zones, odbs, speed profiles, vlans)
  useEffect(() => {
    smartoltService
      .getTopology()
      .then(setTopology)
      .catch((err) => console.warn("Failed to load topology:", err));
  }, []);

  const openActionModal = (type: ModalType, customContext?: Partial<ModalContextData>) => {
    if (!profile?.mapping?.exists || !profile?.mapping?.onu_external_id) {
      toast.error("Map customer to an ONU first");
      return;
    }
    const primaryWifi = (profile?.configuration?.wifi_ports || [])[0];
    const sp = (profile?.configuration?.service_ports || [])[0];
    const wan = profile?.configuration?.wan;

    setModalContext({
      onuExternalId: profile.mapping.onu_external_id,
      onuName: cards?.onu_name || undefined,
      currentSn: profile.mapping.sn || cards?.sn || undefined,
      currentWifi: {
        port: primaryWifi?.port || "1",
        ssid: primaryWifi?.ssid || "",
        password: primaryWifi?.password || "",
        auth_mode: primaryWifi?.auth_mode || "wpa2-psk",
      },
      currentSpeed: {
        upload_profile: sp?.upload_speed || undefined,
        download_profile: sp?.download_speed || undefined,
      },
      currentLocation: {
        zone: cards?.zone_name || undefined,
        odb: cards?.odb_name || undefined,
        address: (cards as any)?.address || undefined,
      },
      currentWan: {
        mode: wan?.mode || undefined,
        username: wan?.pppoe_username || cards?.pppoe_username || undefined,
        ip: wan?.mgmt_ip_address || cards?.mgmt_ip || undefined,
        mask: wan?.mgmt_ip_subnet_mask || undefined,
        gateway: wan?.mgmt_ip_default_gateway || undefined,
        dns1: wan?.mgmt_ip_dns1 || undefined,
      },
      ...(customContext || {}),
    });
    setActiveModal(type);
  };

  const openOnuWebAdmin = async () => {
    const onuExternalId = profile?.mapping?.onu_external_id;
    const mgmtIp = wanInfo?.mgmt_ip_address || cards?.mgmt_ip;
    if (!onuExternalId) {
      toast.error("ONU mapping is required to open Web Admin");
      return;
    }
    if (!mgmtIp) {
      toast.error("Management IP is not available for this ONU");
      return;
    }
    setWebAdminBusy(true);
    try {
      const { mgmt_ip } = await openOnuWebAdminLink(() => smartoltService.getOnuWebAdminLink(onuExternalId));
      toast.success(`Opening ONU Web Admin (${mgmt_ip})`);
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.error || "Unable to open ONU Web Admin");
    } finally {
      setWebAdminBusy(false);
    }
  };

  const handleModalSubmit = async (operationKey: string, payload: Record<string, any>) => {
    setBusy(true);
    try {
      await smartoltService.submitCustomerAction(customerId, operationKey, payload);
      await smartoltService.runCommandsNow(5);
      toast.success("Command queued and dispatched to CPE ONU");
      setActiveModal(null);
      await load();
      // Schedule background live refresh to fetch updated ONU states
      setTimeout(() => {
        smartoltService
          .refreshCustomerFromSmartOLT(customerId, { force: true })
          .then(() => load())
          .catch(() => {});
      }, 1500);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to execute CPE ONU action");
    } finally {
      setBusy(false);
    }
  };

  const toggleWifiRadio = async (port: string, currentAdminState?: string | null) => {
    const isCurrentlyEnabled = (currentAdminState || "").toLowerCase() === "enabled";
    const opKey = isCurrentlyEnabled
      ? "shutdown_onu_wifi_port_by_external_id"
      : "set_onu_wifi_port_mode_lan_by_external_id";
    const payload = isCurrentlyEnabled
      ? { form_data: { wifi_port: port } }
      : { form_data: { wifi_port: port, dhcp: "enabled" } };
    await handleModalSubmit(opKey, payload);
  };

  const toggleCatv = async () => {
    const isCatvEnabled = (cards?.catv_status || "").toLowerCase().includes("enable");
    const opKey = isCatvEnabled ? "disable_onu_catv_by_external_id" : "enable_onu_catv_by_external_id";
    await handleModalSubmit(opKey, {});
  };

  const runAction = async (actionKey?: string) => {
    const key = actionKey || selectedAction;
    if (!profile?.mapping?.exists) {
      toast.error("Map customer to an ONU first");
      return;
    }
    if (key.includes("disable_")) {
      openActionModal("confirm_destructive", {
        destructiveAction: {
          key: "disable_onu_by_external_id",
          title: "Disable ONU",
          description:
            "This will administratively disable the customer ONU. The customer will lose network access until re-enabled.",
          confirmKeyword: "DISABLE",
          buttonLabel: "Disable ONU",
        },
      });
      return;
    }
    setBusy(true);
    try {
      await smartoltService.submitCustomerAction(customerId, key, {});
      await smartoltService.runCommandsNow(5);
      toast.success("Command queued and dispatched to CPE ONU");
      await load();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to execute CPE ONU action");
    } finally {
      setBusy(false);
    }
  };

  // Signal rows for sync history
  const signalRows = useMemo(() => {
    const rxSeries = graphs?.series?.signal_rx || [];
    const txSeries = graphs?.series?.signal_tx || [];
    const length = Math.max(rxSeries.length, txSeries.length);
    return Array.from({ length }).map((_, idx) => ({
      rx: rxSeries[idx],
      tx: txSeries[idx],
    }));
  }, [graphs]);

  // Active Wi-Fi networks
  const activeWifiPorts = useMemo(() => {
    const all = profile?.configuration?.wifi_ports || [];
    const enabled = all.filter((p: SmartOLTWifiPort) => p.ssid || p.admin_state === "Enabled");
    return enabled.length > 0 ? enabled : all.slice(0, 2);
  }, [profile]);

  // Active Ethernet LAN ports
  const ethernetPorts = useMemo(() => {
    return (profile?.configuration?.ethernet_ports || []) as SmartOLTEthernetPort[];
  }, [profile]);

  // Wan & Network info
  const wanInfo = profile?.configuration?.wan_info;
  const cards = profile?.cards;
  const optical = cards?.optical_health;

  // Optical meter needle position (-35 dBm to -10 dBm mapped to 0% - 100%)
  const signalGaugePercent = useMemo(() => {
    const rx = optical?.rx_power ?? (cards?.signal_rx != null ? Number(cards.signal_rx) : null);
    if (rx == null || isNaN(rx)) return null;
    const min = -35;
    const max = -10;
    const clamped = Math.max(min, Math.min(max, rx));
    return ((clamped - min) / (max - min)) * 100;
  }, [optical, cards]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-8 shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3 text-slate-500 py-10">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-sm font-medium">Connecting to CPE ONU and fetching live optical telemetry...</span>
        </div>
      </div>
    );
  }

  const isOnline = (cards?.status || "").toLowerCase() === "online";
  const isAdminEnabled = (cards?.admin_status || "").toLowerCase() === "enabled";

  return (
    <div className="space-y-5">
      {/* ─── 1. TOP HEADER & SYNC BAR ─── */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 transition-all">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white shadow-sm shadow-blue-500/20">
              <Server className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900">CPE ONU Details</h3>
                {profile?.mapping?.exists ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Mapped ({profile.mapping.onu_external_id})
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <AlertTriangle className="h-3 w-3" />
                    Unmapped Customer
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {cards?.olt_name ? `OLT: ${cards.olt_name}` : "CPE ONU"} · Board/Port: {cards?.board || "—"}/{cards?.port || "—"} · ONU: #{cards?.onu_number || "—"}
              </p>
            </div>
          </div>

          {/* Sync status & Refresh actions */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-between sm:justify-end">
            <div className="text-right hidden sm:block">
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Sync Telemetry</p>
              {autoSyncing ? (
                <p className="text-xs font-semibold text-blue-600 flex items-center gap-1 justify-end">
                  <RefreshCw className="h-3 w-3 animate-spin text-blue-600" /> Live Syncing...
                </p>
              ) : (
                <p className="text-xs font-semibold text-slate-700">
                  {cards?.sync_age_minutes === 0
                    ? "Live (just now)"
                    : cards?.sync_age_minutes != null
                    ? `${cards.sync_age_minutes} min ago`
                    : "Never synced"}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={refreshFromSmartOLT}
              disabled={refreshing || !profile?.mapping?.exists}
              className="inline-flex items-center gap-2 h-9 px-3.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200/80 rounded-lg hover:bg-blue-100/80 active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none"
              title="Refresh live optical signals, Wi-Fi configuration and ONU details from CPE ONU"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Syncing..." : "Live Refresh"}
            </button>

            <button
              type="button"
              onClick={() => setShowMappingEditor((prev) => !prev)}
              className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-all"
            >
              Mapping
              {showMappingEditor ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* Suggestion Banner if unmapped */}
        {!profile?.mapping?.exists && profile?.suggested_mapping?.onu_external_id && (
          <div className="mt-4 p-3.5 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <span>
                Found matching ONU <strong>{profile.suggested_mapping.onu_external_id}</strong> for customer{" "}
                <strong>{profile.customer?.internet_id}</strong> ({profile.suggested_mapping.reason})
              </span>
            </div>
            <button
              type="button"
              className="h-8 px-3.5 rounded-lg border border-amber-300 bg-white font-semibold text-xs text-amber-900 hover:bg-amber-100 shadow-sm transition-all"
              onClick={applySuggestedMapping}
            >
              Link Suggestion
            </button>
          </div>
        )}

        {/* Collapsible Mapping Editor */}
        {showMappingEditor && (
          <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                CPE ONU Mapping
              </span>
              <span className="text-[11px] text-slate-500">Links customer record with live CPE ONU</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Unique external ID *</label>
                <input
                  className="h-9 w-full border border-slate-300 bg-white rounded-lg px-2.5 text-xs text-slate-800 font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  value={mappingForm.onu_external_id}
                  onChange={(e) => setMappingForm((prev) => ({ ...prev, onu_external_id: e.target.value }))}
                  placeholder="e.g. HWTCF17D67A4"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Serial Number (SN)</label>
                <input
                  className="h-9 w-full border border-slate-300 bg-white rounded-lg px-2.5 text-xs text-slate-800 font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  value={mappingForm.sn}
                  onChange={(e) => setMappingForm((prev) => ({ ...prev, sn: e.target.value }))}
                  placeholder="Optional SN"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">MAC Address</label>
                <input
                  className="h-9 w-full border border-slate-300 bg-white rounded-lg px-2.5 text-xs text-slate-800 font-mono focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  value={mappingForm.mac}
                  onChange={(e) => setMappingForm((prev) => ({ ...prev, mac: e.target.value }))}
                  placeholder="Optional MAC"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  className="h-9 w-full bg-blue-600 text-white font-semibold text-xs rounded-lg hover:bg-blue-700 shadow-sm inline-flex items-center justify-center gap-1.5 active:scale-98 transition-all"
                  onClick={() => saveMapping(false)}
                  disabled={busy}
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Mapping
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── 2. EXECUTIVE 5-METRIC NOC KPI STRIP ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
        {/* Card 1: Status */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Status</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => openActionModal("ont_swap")}
                title="Swap ONU (Change SN)"
                className="p-1 rounded-md text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-all"
              >
                <Cpu className="h-3.5 w-3.5" />
              </button>
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isOnline ? "bg-emerald-500 shadow-sm shadow-emerald-500/50 animate-pulse" : "bg-rose-500"
                }`}
              />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className={`text-lg font-bold capitalize ${
                isOnline ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {cards?.status || "—"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">
            ONU Type: <strong className="text-slate-700 font-medium">{cards?.onu_type_name || "GPON ONU"}</strong>
          </p>
        </div>

        {/* Card 2: Administrative Status */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Administrative status</span>
            <ShieldCheck
              className={`h-4 w-4 ${isAdminEnabled ? "text-emerald-600" : "text-amber-500"}`}
            />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className={`text-lg font-bold ${
                isAdminEnabled ? "text-slate-900" : "text-amber-700"
              }`}
            >
              {cards?.admin_status || "—"}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
            <span className="truncate">
              CATV: <strong className="text-slate-700">{cards?.catv_status || "Disabled"}</strong>
            </span>
            {cards?.catv_status && !cards.catv_status.includes("Not Supported") && (
              <button
                type="button"
                onClick={toggleCatv}
                title="Toggle CATV RF Port"
                className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-600 transition-all shrink-0"
              >
                Toggle
              </button>
            )}
          </div>
        </div>

        {/* Card 3: Optical Signal */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Optical Signal</span>
            <Radio
              className={`h-4 w-4 ${
                optical?.color === "emerald"
                  ? "text-emerald-600"
                  : optical?.color === "amber"
                  ? "text-amber-600"
                  : "text-rose-600"
              }`}
            />
          </div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span
              className={`text-lg font-bold font-mono ${
                optical?.color === "emerald"
                  ? "text-emerald-700"
                  : optical?.color === "amber"
                  ? "text-amber-700"
                  : "text-rose-700"
              }`}
            >
              {cards?.signal_rx != null ? `${cards.signal_rx} dBm` : cards?.signal_1490 ? `${cards.signal_1490} dBm` : "—"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">
            Rating:{" "}
            <strong
              className={`font-semibold ${
                optical?.color === "emerald"
                  ? "text-emerald-600"
                  : optical?.color === "amber"
                  ? "text-amber-600"
                  : "text-rose-600"
              }`}
            >
              {optical?.label || cards?.signal_quality || "Unknown"}
            </strong>
          </p>
        </div>

        {/* Card 4: Optical Distance & ODB */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Optical Distance</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => openActionModal("location")}
                title="Change Zone / ODB"
                className="p-1 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
              >
                <MapPin className="h-3.5 w-3.5" />
              </button>
              <Cable className="h-4 w-4 text-indigo-600" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-lg font-bold text-slate-900 font-mono">
              {cards?.distance != null ? `${cards.distance} m` : "—"}
            </span>
            <span className="text-xs text-slate-400">optical</span>
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate" title={cards?.odb_name || "Unassigned"}>
            ODB: <strong className="text-slate-700 font-medium">{cards?.odb_name || "Unassigned"}</strong>
          </p>
        </div>

        {/* Card 5: Speed Profiles */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Speed Profiles</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => openActionModal("speed")}
                title="Change Speed Profiles"
                className="p-1 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
              >
                <Sliders className="h-3.5 w-3.5" />
              </button>
              <Gauge className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-slate-900 font-mono">
              {cards?.bandwidth || "1G Profile"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">
            VLAN: <strong className="text-slate-700 font-medium">{cards?.vlan || "Default"}</strong> · Zone:{" "}
            <span className="text-slate-600">{cards?.zone_name || "Standard"}</span>
          </p>
        </div>
      </div>

      {/* ─── 3. OPTICAL POWER BUDGET GAUGE & ATTENUATION ANALYSIS ─── */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-5 shadow-md">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-slate-700/60">
          <div>
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-cyan-400" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-200">
                Optical Signal Power
              </h4>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Standard Class B+ / C+ GPON Window · Rx Sensitivity Cutoff: -27.00 dBm · Overload Ceiling: -8.00 dBm
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 rounded-lg bg-slate-800/80 border border-slate-700 text-xs">
              <span className="text-slate-400">Downstream 1490nm (Rx): </span>
              <strong className="text-cyan-400 font-mono">{cards?.signal_1490 ? `${cards.signal_1490} dBm` : "—"}</strong>
            </div>
            <div className="px-3 py-1 rounded-lg bg-slate-800/80 border border-slate-700 text-xs">
              <span className="text-slate-400">Upstream 1310nm (Tx): </span>
              <strong className="text-purple-300 font-mono">{cards?.signal_1310 ? `${cards.signal_1310} dBm` : "—"}</strong>
            </div>
          </div>
        </div>

        {/* Visual Attenuation Gauge Bar */}
        <div className="mt-5 space-y-2">
          <div className="relative h-6 rounded-full bg-slate-800 p-1 flex overflow-hidden border border-slate-700">
            {/* Zone 1: Critical / LOS (< -27 dBm) */}
            <div className="h-full w-[32%] bg-gradient-to-r from-rose-600 to-rose-500 rounded-l-full relative group">
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white/90">
                Critical (&lt; -27 dBm)
              </span>
            </div>
            {/* Zone 2: Warning / High Attenuation (-27 to -24 dBm) */}
            <div className="h-full w-[12%] bg-gradient-to-r from-amber-500 to-amber-400 relative group">
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-amber-950">
                Warning
              </span>
            </div>
            {/* Zone 3: Optimal (-24 to -8 dBm) */}
            <div className="h-full w-[56%] bg-gradient-to-r from-emerald-500 to-teal-400 rounded-r-full relative group">
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-teal-950">
                Optimal GPON Window (-24 to -8 dBm)
              </span>
            </div>

            {/* Needle Marker */}
            {signalGaugePercent !== null && (
              <div
                className="absolute top-0 bottom-0 w-2.5 -ml-1.25 bg-white shadow-lg shadow-white/50 rounded-full border border-slate-900 z-10 transition-all duration-500"
                style={{ left: `${signalGaugePercent}%` }}
                title={`Current Rx: ${cards?.signal_rx ?? cards?.signal_1490} dBm`}
              />
            )}
          </div>

          {/* Scale Labels */}
          <div className="flex justify-between text-[10px] text-slate-400 font-mono px-1">
            <span>-35.0 dBm (LOS)</span>
            <span className="text-amber-300">-27.0 dBm (Boundary)</span>
            <span className="text-emerald-300">-24.0 dBm (Healthy)</span>
            <span>-15.0 dBm</span>
            <span>-8.0 dBm (Overload)</span>
          </div>
        </div>

        {/* Diagnosis message */}
        <div className="mt-4 p-3 rounded-xl bg-slate-800/60 border border-slate-700/80 text-xs flex items-start gap-2.5">
          <AlertTriangle
            className={`h-4 w-4 shrink-0 mt-0.5 ${
              optical?.color === "emerald"
                ? "text-emerald-400"
                : optical?.color === "amber"
                ? "text-amber-400"
                : "text-rose-400"
            }`}
          />
          <div className="space-y-0.5">
            <p className="font-semibold text-slate-200">
              {optical?.message || "Optical power reading is within standard operating parameters."}
            </p>
            {optical?.status === "warning" && (
              <p className="text-slate-400">
                Technician Recommendation: Inspect drop fiber patch cord at customer premises, check optical connector at ODB <strong>{cards?.odb_name || "ODB"}</strong>, and verify splice integrity.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─── 4. WI-FI NETWORKS & PHYSICAL LAN PORTS ─── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Wi-Fi Networks Card */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Wifi className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">WiFi Ports</h4>
                <p className="text-[11px] text-slate-500">SSID configuration provisioned on customer ONU</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openActionModal("wifi")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-all"
              >
                <Sliders className="h-3 w-3" /> Configure WiFi
              </button>
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                {activeWifiPorts.length} {activeWifiPorts.length === 1 ? "SSID" : "SSIDs"}
              </span>
            </div>
          </div>

          {activeWifiPorts.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              <WifiOff className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No Wi-Fi networks configured or ONU is operating in pure bridge mode.
            </div>
          ) : (
            <div className="space-y-3">
              {activeWifiPorts.map((wp, idx) => {
                const isRevealed = visiblePasswords[wp.port];
                const is5G = (wp.ssid || "").toLowerCase().includes("5g") || wp.port.includes("/5");
                const isRadioOn = (wp.admin_state || "").toLowerCase() === "enabled";
                return (
                  <div
                    key={wp.port || idx}
                    className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition-all space-y-2.5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-200/80 text-slate-700">
                          {is5G ? "5.0 GHz" : "2.4 GHz"}
                        </span>
                        <span className="text-xs font-bold text-slate-900">{wp.ssid || "Hidden SSID"}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => toggleWifiRadio(wp.port, wp.admin_state)}
                          title={isRadioOn ? "Disable Wi-Fi Radio" : "Enable Wi-Fi Radio"}
                          className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-all"
                        >
                          {isRadioOn ? (
                            <WifiOff className="h-3.5 w-3.5 text-rose-500" />
                          ) : (
                            <Wifi className="h-3.5 w-3.5 text-emerald-600" />
                          )}
                        </button>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                            isRadioOn
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-200 text-slate-600 border-slate-300"
                          }`}
                        >
                          {wp.admin_state || "Enabled"}
                        </span>
                      </div>
                    </div>

                    {/* Password row with Eye toggle & Copy */}
                    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 font-medium text-[11px]">Key:</span>
                        <span className="font-mono font-semibold text-slate-800 tracking-wider">
                          {wp.password
                            ? isRevealed
                              ? wp.password
                              : "••••••••••••"
                            : "••••••••••••"}
                        </span>
                      </div>
                      {wp.password && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility(wp.port)}
                            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
                            title={isRevealed ? "Mask password" : "Show password"}
                          >
                            {isRevealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopy(wp.port, wp.password!, "Wi-Fi password")}
                            className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                            title="Copy Wi-Fi password"
                          >
                            {copiedKey === wp.port ? (
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-0.5">
                      <span>Auth: {wp.auth_mode ? wp.auth_mode.toUpperCase() : "WPA2-PSK"}</span>
                      <span>Interface: {wp.port}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Physical Ethernet Ports Card */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Network className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Ethernet Ports</h4>
                <p className="text-[11px] text-slate-500">LAN Ethernet interfaces on customer ONU</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openActionModal("lan_port")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-all"
              >
                <Sliders className="h-3 w-3" /> Configure LAN Ports
              </button>
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                {ethernetPorts.length} Ports
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {ethernetPorts.length === 0 ? (
              <div className="col-span-4 p-6 text-center text-slate-400 text-xs">
                No physical Ethernet ports reported by CPE ONU.
              </div>
            ) : (
              ethernetPorts.map((ep, idx) => {
                const isPortEnabled = (ep.admin_state || "").toLowerCase() === "enabled";
                return (
                  <div
                    key={ep.port || idx}
                    className="p-3 rounded-xl border border-slate-200 bg-slate-50/60 flex flex-col items-center justify-center text-center space-y-1.5"
                  >
                    <div
                      className={`h-7 w-7 rounded-lg flex items-center justify-center ${
                        isPortEnabled
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      <Router className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs font-bold text-slate-800 font-mono uppercase">{ep.port}</span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        isPortEnabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {ep.admin_state || "Active"}
                    </span>
                    <span className="text-[10px] text-slate-400">{ep.mode || "LAN"} Mode</span>
                  </div>
                );
              })
            )}
          </div>

          {/* Service Ports / Bandwidth Table */}
          <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Service Ports
              </span>
              <button
                type="button"
                onClick={() => openActionModal("speed")}
                className="text-[10px] font-semibold text-blue-600 hover:text-blue-800"
              >
                Change Speed Profiles →
              </button>
            </div>
            {(profile?.configuration?.service_ports || []).map((sp, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-xs"
              >
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-mono font-bold text-[10px]">
                    Port #{sp.service_port || "1282"}
                  </span>
                  <span className="text-slate-700">VLAN {sp.vlan || "531"}</span>
                </div>
                <div className="flex items-center gap-3 font-mono font-semibold text-slate-800">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <ArrowDown className="h-3 w-3" /> {sp.download_speed || "1G"}
                  </span>
                  <span className="flex items-center gap-1 text-blue-600">
                    <ArrowUp className="h-3 w-3" /> {sp.upload_speed || "1G"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── 5. NETWORK & WAN PROVISIONING DETAILS ─── */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Network className="h-4 w-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">WAN &amp; Network Configuration</h4>
              <p className="text-[11px] text-slate-500">IP routing, PPPoE credentials, VLAN, and OLT termination</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openActionModal("wan")}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-all"
            >
              <Globe className="h-3 w-3" /> Configure WAN
            </button>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-mono">
              VLAN {wanInfo?.vlan || cards?.vlan || "531"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* PPPoE Username */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">PPPoE Username</span>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs font-bold text-slate-900 font-mono truncate">
                {wanInfo?.pppoe_username || cards?.pppoe_username || "—"}
              </span>
              {(wanInfo?.pppoe_username || cards?.pppoe_username) && (
                <button
                  type="button"
                  onClick={() =>
                    handleCopy("pppoe", wanInfo?.pppoe_username || cards?.pppoe_username || "", "PPPoE Username")
                  }
                  className="p-1 text-slate-400 hover:text-blue-600"
                >
                  {copiedKey === "pppoe" ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          </div>

          {/* Management IP */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Management IP</span>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs font-bold text-slate-900 font-mono truncate">
                {wanInfo?.mgmt_ip_address || cards?.mgmt_ip || "—"}
              </span>
              {(wanInfo?.mgmt_ip_address || cards?.mgmt_ip) && (
                <div className="ml-2 flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    onClick={openOnuWebAdmin}
                    disabled={webAdminBusy || !profile?.mapping?.onu_external_id}
                    title="Open ONU Web Admin"
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {webAdminBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin text-blue-600" /> : <ExternalLink className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleCopy("mgmt_ip", wanInfo?.mgmt_ip_address || cards?.mgmt_ip || "", "Management IP")
                    }
                    title="Copy Management IP"
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    {copiedKey === "mgmt_ip" ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Gateway & Subnet */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Default Gateway</span>
            <p className="text-xs font-bold text-slate-900 font-mono mt-1 truncate">
              {wanInfo?.mgmt_ip_default_gateway || "10.110.100.1"}
            </p>
          </div>

          {/* Subnet Mask */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Subnet Mask</span>
            <p className="text-xs font-bold text-slate-900 font-mono mt-1 truncate">
              {wanInfo?.mgmt_ip_subnet_mask || "255.255.252.0"}
            </p>
          </div>

          {/* DNS Servers */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">DNS 1 / DNS 2</span>
            <p className="text-xs font-bold text-slate-900 font-mono mt-1 truncate">
              {wanInfo?.mgmt_ip_dns1 || "8.8.8.8"} · {wanInfo?.mgmt_ip_dns2 || "8.8.4.4"}
            </p>
          </div>

          {/* WAN Mode */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">WAN Mode</span>
            <p className="text-xs font-bold text-slate-900 capitalize mt-1 truncate">
              {wanInfo?.mode || "Route"}
            </p>
          </div>
        </div>
      </div>

      {/* ─── 6. LIVE OPTICAL GRAPHS & TELEMETRY ─── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="space-y-3">
          {/* Sub-tab Navigation */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-xl w-fit border border-slate-200/60">
            <button
              type="button"
              onClick={() => setActiveGraphTab("historical")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeGraphTab === "historical"
                  ? "bg-white text-deep-ocean shadow-xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Activity className="h-3.5 w-3.5 text-indigo-600" />
              MRTG Graphs
            </button>
            <button
              type="button"
              onClick={() => setActiveGraphTab("live_test")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeGraphTab === "live_test"
                  ? "bg-white text-emerald-700 shadow-xs"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Zap className="h-3.5 w-3.5 text-emerald-600" />
              Live Traffic &amp; Speed Test
            </button>
          </div>

          {activeGraphTab === "historical" ? (
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-indigo-600" />
                  <h4 className="text-sm font-bold text-slate-900">Signal &amp; Traffic Graphs</h4>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="h-8 border border-slate-200 rounded-lg px-2 text-xs font-medium text-slate-700 bg-white"
                    value={graphKind}
                    onChange={(e) => setGraphKind(e.target.value as "signal" | "traffic")}
                  >
                    <option value="traffic">Traffic</option>
                    <option value="signal">Signal</option>
                  </select>
                  <select
                    className="h-8 border border-slate-200 rounded-lg px-2 text-xs font-medium text-slate-700 bg-white"
                    value={graphType}
                    onChange={(e) => setGraphType(e.target.value as GraphType)}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <select
                    className="h-8 border border-slate-200 rounded-lg px-2 text-xs font-medium text-slate-700 bg-white"
                    value={range}
                    onChange={(e) => setRange(e.target.value as any)}
                  >
                    <option value="6h">Sync 6h</option>
                    <option value="24h">Sync 24h</option>
                    <option value="7d">Sync 7d</option>
                    <option value="30d">Sync 30d</option>
                  </select>
                </div>
              </div>

              <div className="relative p-4 bg-slate-50/50 min-h-[260px] flex items-center justify-center">
                {!profile?.mapping?.exists ? (
                  <div className="text-sm text-slate-400">Map this customer to an ONU to view CPE ONU optical graphs.</div>
                ) : graphImageUrl ? (
                  <>
                    <img
                      src={graphImageUrl}
                      alt={`${graphKind} graph ${graphType}`}
                      className="w-full max-h-80 object-contain rounded-xl border border-slate-200/80 bg-white shadow-sm"
                    />
                    {graphImageLoading && (
                      <div className="absolute inset-4 rounded-xl bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm">
                          <RefreshCw className="h-3.5 w-3.5 animate-spin text-indigo-600" />
                          Updating graph...
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="inline-flex items-center gap-2 text-xs text-slate-400">
                    {graphImageLoading && <RefreshCw className="h-3.5 w-3.5 animate-spin text-indigo-600" />}
                    Rendering graph telemetry from CPE ONU...
                  </div>
                )}
              </div>

              <div className="border-t border-slate-100 max-h-48 overflow-auto">
                {graphLoading ? (
                  <div className="px-5 py-4 text-xs text-slate-500 text-center">
                    Updating cached telemetry points for this window...
                  </div>
                ) : signalRows.length === 0 ? (
                  <div className="px-5 py-4 text-xs text-slate-400 text-center">
                    No telemetry data points recorded for this window.
                  </div>
                ) : (
                  signalRows.slice(-30).map((row, idx) => (
                    <div
                      key={idx}
                      className="px-5 py-2 text-xs border-b border-slate-100/60 grid grid-cols-3 hover:bg-slate-50 transition-all font-mono"
                    >
                      <span className="text-slate-500 text-[11px] font-sans">
                        {row.rx?.ts ? new Date(row.rx.ts).toLocaleString() : "—"}
                      </span>
                      <span className="text-slate-800">
                        1490nm (Rx): <strong>{row.rx?.value ?? "—"} dBm</strong>
                      </span>
                      <span className="text-slate-800">
                        1310nm (Tx): <strong>{row.tx?.value ?? "—"} dBm</strong>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <LiveTrafficTestConsole
              customerId={customerId}
              onuExternalId={profile?.mapping?.onu_external_id}
              targetLabel={profile?.cards?.onu_name || customerId}
            />
          )}
        </div>

        {/* Action Panel & Commands */}
        <div className="space-y-5">
          {/* Action Console */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-slate-900 text-cyan-400 shadow-xs">
                  <Router className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    ONU Actions &amp; Operations
                  </h4>
                  <p className="text-[11px] text-slate-500 font-medium">Direct OLT / OMCI operations</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shadow-2xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  OMCI Linked
                </span>
              </div>
            </div>

            {/* Telemetry Metadata Readout Chips */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="p-2.5 rounded-xl bg-slate-50/80 border border-slate-200/80 flex items-start gap-2.5 transition-all hover:bg-white hover:shadow-2xs">
                <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 mt-0.5 shrink-0">
                  <Server className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">OLT Name</span>
                  <strong className="text-slate-800 text-xs font-bold truncate block" title={cards?.olt_name || cards?.olt_id || "—"}>
                    {cards?.olt_name || cards?.olt_id || "—"}
                  </strong>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-slate-50/80 border border-slate-200/80 flex items-start gap-2.5 transition-all hover:bg-white hover:shadow-2xs">
                <div className="p-1.5 rounded-lg bg-cyan-50 text-cyan-600 mt-0.5 shrink-0">
                  <Cable className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">PON Location</span>
                  <strong className="text-slate-800 text-xs font-mono font-bold truncate block">
                    Slot {cards?.board || "—"} / Port {cards?.port || "—"}
                  </strong>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-slate-50/80 border border-slate-200/80 flex items-start gap-2.5 transition-all hover:bg-white hover:shadow-2xs">
                <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 mt-0.5 shrink-0">
                  <Router className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">ONU Name</span>
                  <strong className="text-slate-800 text-xs font-bold truncate block" title={cards?.onu_name || "—"}>
                    {cards?.onu_name || "—"}
                  </strong>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-slate-50/80 border border-slate-200/80 flex items-start gap-2.5 transition-all hover:bg-white hover:shadow-2xs">
                <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600 mt-0.5 shrink-0">
                  <MapPin className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">ODB Splitter</span>
                  <strong className="text-slate-800 text-xs font-bold truncate block" title={cards?.odb_name || "—"}>
                    {cards?.odb_name || "—"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Categorized Segmented Tab Navigation with Zero Raw Outlines */}
            <div className="p-1 bg-slate-100 rounded-xl border border-slate-200/80 grid grid-cols-5 gap-1 text-xs select-none">
              <button
                type="button"
                onClick={() => setActionCategory("quick")}
                className={`py-2 px-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all outline-none focus:outline-none focus:ring-0 ${
                  actionCategory === "quick"
                    ? "bg-white text-blue-600 shadow-xs border border-slate-200/60"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                <Zap className="h-3.5 w-3.5" />
                <span>Quick</span>
              </button>
              <button
                type="button"
                onClick={() => setActionCategory("wan")}
                className={`py-2 px-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all outline-none focus:outline-none focus:ring-0 ${
                  actionCategory === "wan"
                    ? "bg-white text-cyan-600 shadow-xs border border-slate-200/60"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                <Globe className="h-3.5 w-3.5" />
                <span>WAN</span>
              </button>
              <button
                type="button"
                onClick={() => setActionCategory("lan")}
                className={`py-2 px-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all outline-none focus:outline-none focus:ring-0 ${
                  actionCategory === "lan"
                    ? "bg-white text-indigo-600 shadow-xs border border-slate-200/60"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                <Cable className="h-3.5 w-3.5" />
                <span>Ports</span>
              </button>
              <button
                type="button"
                onClick={() => setActionCategory("security")}
                className={`py-2 px-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all outline-none focus:outline-none focus:ring-0 ${
                  actionCategory === "security"
                    ? "bg-white text-rose-600 shadow-xs border border-slate-200/60"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                <Lock className="h-3.5 w-3.5" />
                <span>Security</span>
              </button>
              <button
                type="button"
                onClick={() => setActionCategory("services")}
                className={`py-2 px-1.5 rounded-lg font-bold flex items-center justify-center gap-1.5 transition-all outline-none focus:outline-none focus:ring-0 ${
                  actionCategory === "services"
                    ? "bg-white text-emerald-600 shadow-xs border border-slate-200/60"
                    : "text-slate-500 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                <Layers className="h-3.5 w-3.5" />
                <span>Services</span>
              </button>
            </div>

            {/* Action Buttons for selected category */}
            {actionCategory === "quick" && (
              <div className="space-y-2.5 pt-1">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => runAction("reboot_onu_by_external_id")}
                    disabled={busy || !profile?.mapping?.exists}
                    className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-blue-50/70 hover:border-blue-300 text-left transition-all shadow-2xs group disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 group-hover:scale-105 transition-transform">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-xs font-bold text-slate-800 group-hover:text-blue-700">Reboot ONU</span>
                    </div>
                    <span className="text-[10px] text-slate-400 block pl-8">Reboot ONU via OMCI</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => runAction("resync_onu_config_by_external_id")}
                    disabled={busy || !profile?.mapping?.exists}
                    className="p-3 rounded-xl border border-slate-200 bg-white hover:bg-indigo-50/70 hover:border-indigo-300 text-left transition-all shadow-2xs group disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 group-hover:scale-105 transition-transform">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </div>
                      <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-700">Resync config</span>
                    </div>
                    <span className="text-[10px] text-slate-400 block pl-8">Resync ONU configuration</span>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => runAction("enable_onu_by_external_id")}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-emerald-200 bg-emerald-50/80 hover:bg-emerald-100 text-xs font-bold text-emerald-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    <span>Enable ONU</span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      openActionModal("confirm_destructive", {
                        destructiveAction: {
                          key: "disable_onu_by_external_id",
                          title: "Disable ONU",
                          description:
                            "This will administratively disable the customer ONU. The customer will lose network access until re-enabled.",
                          confirmKeyword: "DISABLE",
                          buttonLabel: "Disable ONU",
                        },
                      })
                    }
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-rose-200 bg-rose-50/80 hover:bg-rose-100 text-xs font-bold text-rose-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <AlertTriangle className="h-4 w-4 text-rose-600" />
                    <span>Disable ONU</span>
                  </button>
                </div>
              </div>
            )}

            {actionCategory === "wan" && (
              <div className="space-y-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => openActionModal("wan")}
                  disabled={busy || !profile?.mapping?.exists}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white text-xs font-bold shadow-sm transition-all disabled:opacity-50 outline-none focus:outline-none"
                >
                  <Globe className="h-4 w-4" />
                  Set WAN Mode (PPPoE / DHCP / Static IP)
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleModalSubmit("enable_onu_allow_remote_access_wan_ip_by_external_id", {})}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-cyan-50/60 hover:border-cyan-200 text-xs font-semibold text-slate-700 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Key className="h-3.5 w-3.5 text-cyan-600" />
                    Allow Remote Access to WAN IP
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModalSubmit("disable_onu_allow_remote_access_wan_ip_by_external_id", {})}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-xs font-semibold text-slate-700 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Lock className="h-3.5 w-3.5 text-slate-500" />
                    Disable Remote Access to WAN IP
                  </button>
                </div>
              </div>
            )}

            {actionCategory === "lan" && (
              <div className="space-y-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => openActionModal("lan_port")}
                  disabled={busy || !profile?.mapping?.exists}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white text-xs font-bold shadow-sm transition-all disabled:opacity-50 outline-none focus:outline-none"
                >
                  <Cable className="h-4 w-4" />
                  Configure LAN Port Modes &amp; VLANs
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleModalSubmit("enable_onu_ip_dhcp_snooping_by_external_id", {})}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-indigo-50/60 hover:border-indigo-200 text-xs font-semibold text-slate-700 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Check className="h-3.5 w-3.5 text-indigo-600" />
                    Enable IP DHCP Snooping
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModalSubmit("disable_onu_ip_dhcp_snooping_by_external_id", {})}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-xs font-semibold text-slate-700 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <X className="h-3.5 w-3.5 text-slate-400" />
                    Disable IP DHCP Snooping
                  </button>
                </div>
              </div>
            )}

            {actionCategory === "security" && (
              <div className="space-y-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => openActionModal("web_password")}
                  disabled={busy || !profile?.mapping?.exists}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                >
                  <Lock className="h-4 w-4 text-rose-600" />
                  Change ONU Web Admin Password
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      openActionModal("confirm_destructive", {
                        destructiveAction: {
                          key: "restore_onu_factory_defaults_by_external_id",
                          title: "Restore factory defaults",
                          description:
                            "WARNING: This will restore factory defaults on the customer ONU. Wi-Fi credentials and local settings will be reset. The device will reboot to factory default state.",
                          confirmKeyword: "RESTORE",
                          buttonLabel: "Restore Factory Defaults",
                        },
                      })
                    }
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-amber-200 bg-amber-50/80 hover:bg-amber-100 text-xs font-bold text-amber-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <RefreshCw className="h-3.5 w-3.5 text-amber-600" />
                    Restore Factory Defaults
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      openActionModal("confirm_destructive", {
                        destructiveAction: {
                          key: "delete_onu_by_external_id",
                          title: "Delete ONU",
                          description:
                            "CRITICAL: This will delete the ONU from the OLT control plane. The customer will immediately lose optical authorization and network access.",
                          confirmKeyword: "DELETE",
                          buttonLabel: "Delete ONU",
                        },
                      })
                    }
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border border-rose-200 bg-rose-50/80 hover:bg-rose-100 text-xs font-bold text-rose-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                    Delete ONU
                  </button>
                </div>
              </div>
            )}

            {actionCategory === "services" && (
              <div className="space-y-2.5 pt-1">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={toggleCatv}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-emerald-200 bg-emerald-50/80 hover:bg-emerald-100 text-xs font-bold text-emerald-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Radio className="h-4 w-4 text-emerald-600" />
                    Enable / Disable CATV
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModalSubmit("enable_onu_iptv_by_external_id", {})}
                    disabled={busy || !profile?.mapping?.exists}
                    className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-indigo-200 bg-indigo-50/80 hover:bg-indigo-100 text-xs font-bold text-indigo-800 transition-all shadow-2xs disabled:opacity-50 outline-none focus:outline-none"
                  >
                    <Tv className="h-4 w-4 text-indigo-600" />
                    Enable IPTV
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => openActionModal("speed")}
                  disabled={busy || !profile?.mapping?.exists}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs font-bold shadow-sm transition-all disabled:opacity-50 outline-none focus:outline-none"
                >
                  <Zap className="h-4 w-4" />
                  Change Speed Profiles
                </button>
              </div>
            )}
          </div>

          {/* Command History Audit Log */}
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Command History</h4>
              <span className="text-[11px] text-slate-400">{commands.length} Recorded</span>
            </div>
            <div className="max-h-56 overflow-auto">
              {commands.length === 0 ? (
                <div className="px-5 py-6 text-xs text-slate-400 text-center">No actions executed yet.</div>
              ) : (
                commands.map((item) => (
                  <div key={item.id} className="px-5 py-2.5 border-b border-slate-100/60 hover:bg-slate-50 transition-all">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-800">{item.action_key}</p>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          item.status === "success"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : item.status === "error" || item.status === "failed"
                            ? "bg-rose-50 text-rose-700 border border-rose-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {item.created_at ? new Date(item.created_at).toLocaleString() : "—"} · retries {item.retry_count}/
                      {item.max_retries}
                    </p>
                    {item.error_message && (
                      <p className="text-[11px] text-rose-600 mt-1 bg-rose-50 p-1.5 rounded border border-rose-200">
                        {item.error_message}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── ACTION MODALS SUITE ─── */}
      <SmartOLTActionModals
        activeModal={activeModal}
        onClose={() => setActiveModal(null)}
        onSubmit={handleModalSubmit}
        context={modalContext}
        topology={topology}
        busy={busy}
      />
    </div>
  );
};
